import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';

import { COLORS } from 'src/online-payment/constants';

/* =================== PAYFORMS — SHARED PAGE STATES =================== */

/**
 * Page chrome and the honest non-data states every Payforms page can land on.
 * None of them ever reads as "no forms" / "no customers" / "no payments":
 * an access problem, a provider outage and an ineligible account are each
 * named for what they are.
 */

export function PayformsPageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        mb: 3,
      }}
    >
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: COLORS.textPrimary,
            fontSize: { xs: '1.5rem', sm: '2rem' },
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

const CAPABILITY_COPY: Record<string, string> = {
  VIEW: 'view Payforms',
  MANAGE: 'create or change Payforms',
  PII: 'see sign-ups, customers and payment history',
  CANCEL: 'cancel recurring payments',
};

/** Access V2 said no (or nobody is identified). Hide/show only; never a redirect. */
export function PayformsForbiddenView({
  capability,
  identified,
}: {
  capability: string;
  identified: boolean;
}) {
  return (
    <Card sx={{ p: { xs: 2.5, md: 3.5 } }} data-testid="payforms-forbidden">
      <Stack spacing={1.5}>
        <Typography variant="h6">Payforms is not available to you</Typography>
        <Typography variant="body2" color="text.secondary">
          {identified
            ? `Your role does not allow you to ${CAPABILITY_COPY[capability] ?? 'use this page'}. Ask the business owner to update your access.`
            : 'Payforms needs an identified user. Sign in with your own user account (or identify with your PIN) to continue.'}
        </Typography>
      </Stack>
    </Card>
  );
}

/**
 * Provider or service problem. A failed read is never rendered as "nothing
 * here" — the page says the data could not be loaded and offers a retry.
 */
export function PayformsUnavailableView({
  title = 'This information is temporarily unavailable',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <Alert
      severity="warning"
      data-testid="payforms-unavailable"
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{title}</AlertTitle>
      {message}
    </Alert>
  );
}

export function PayformsLoadingView({ label }: { label: string }) {
  return (
    <Card sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={1.5} aria-busy="true" aria-label={label}>
        <Skeleton variant="text" width="40%" height={34} />
        <Skeleton variant="rounded" height={44} />
        <Skeleton variant="rounded" height={44} />
        <Skeleton variant="rounded" height={44} />
      </Stack>
    </Card>
  );
}
